import { useState, useCallback, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { format, differenceInBusinessDays, differenceInDays } from 'date-fns';
import { calculerJoursOuvres } from '@/lib/heures-utils';
import { useQueryClient } from '@tanstack/react-query';
import { invalidateChantier, invalidateAlertes } from '@/lib/queryInvalidation';
import { useSubscription } from '@/contexts/SubscriptionContext';
import type {
  SuspensionInfo,
  SuspensionFormData,
  RepriseFormData,
  AffectationSuspendue,
  SuspensionType,
} from '@/types/calendar';

interface UseSuspensionProps {
  chantierId: string;
  onSuccess?: () => void;
  initialSuspension?: SuspensionInfo | null;
}

interface AffectationActuelle {
  id: string;
  membre_equipe_id: string;
  equipe_id: string | null;
  date_debut: string;
  date_fin: string;
  heures_par_jour: number;
  taux_horaire_specifique: number | null;
  jours_details: any | null;
  notes?: string;  // Pour identifier le type d'affectation (retard, reprise, normale)
  membre: {
    id: string;
    nom: string;
    prenom: string;
    taux_horaire: number;
  };
}

export function useSuspension({ chantierId, onSuccess, initialSuspension }: UseSuspensionProps) {
  const queryClient = useQueryClient();
  const { entrepriseId } = useSubscription();
  const [loading, setLoading] = useState(false);
  const [suspensionActive, setSuspensionActive] = useState<SuspensionInfo | null>(initialSuspension || null);
  const [affectationsActuelles, setAffectationsActuelles] = useState<AffectationActuelle[]>([]);

  // Mettre à jour suspensionActive quand initialSuspension change
  useEffect(() => {
    if (initialSuspension) {
      setSuspensionActive(initialSuspension);
    }
  }, [initialSuspension]);

  // Charger la suspension active du chantier
  const loadSuspensionActive = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('suspensions_chantiers')
        .select('*')
        .eq('chantier_id', chantierId)
        .is('date_reprise', null)
        .order('date_suspension', { ascending: false })
        .limit(1)
        .single();

      if (error && error.code !== 'PGRST116') {
        console.error('[useSuspension] Erreur chargement suspension:', error);
        return;
      }

      setSuspensionActive(data as SuspensionInfo | null);
    } catch (error) {
      console.error('[useSuspension] Erreur:', error);
    }
  }, [chantierId]);

  // Charger les affectations actuelles du chantier (normales + retard + reprise)
  const loadAffectationsActuelles = useCallback(async () => {
    try {
      const today = format(new Date(), 'yyyy-MM-dd');
      const { data, error } = await supabase
        .from('affectations_chantiers')
        .select(`
          id,
          membre_equipe_id,
          equipe_id,
          date_debut,
          date_fin,
          heures_par_jour,
          taux_horaire_specifique,
          jours_details,
          notes,
          membre:membres_equipe(id, nom, prenom, taux_horaire)
        `)
        .eq('chantier_id', chantierId)
        .gte('date_fin', today)
        .order('date_debut', { ascending: false });

      if (error) throw error;

      // Typer les affectations
      const affectationsRaw = (data || []) as unknown as AffectationActuelle[];

      // Dédupliquer uniquement pour les affectations normales (pas retard/reprise)
      // Les affectations de retard et reprise sont toujours conservées
      const seenMembreIds = new Set<string>();
      const affectationsUniques = affectationsRaw.filter(aff => {
        const isRetard = aff.notes?.startsWith('Retard chantier');
        const isReprise = aff.notes === 'Reprise chantier';

        // Pour retard/reprise : toujours garder (pas de déduplications)
        if (isRetard || isReprise) return true;

        // Pour normales : dédupliquer par membre
        if (aff.membre_equipe_id && seenMembreIds.has(aff.membre_equipe_id)) {
          return false;
        }
        if (aff.membre_equipe_id) {
          seenMembreIds.add(aff.membre_equipe_id);
        }
        return true;
      });

      setAffectationsActuelles(affectationsUniques);
    } catch (error) {
      console.error('[useSuspension] Erreur chargement affectations:', error);
    }
  }, [chantierId]);

  // Créer le snapshot des affectations pour la reprise future
  // Inclut maintenant les infos de type (normale, retard, reprise)
  const creerSnapshotAffectations = useCallback((
    affectations: AffectationActuelle[],
    membresASuspendre?: string[] // Si partielle, seulement ces membres
  ): AffectationSuspendue[] => {
    const affectationsFiltered = membresASuspendre
      ? affectations.filter(a => membresASuspendre.includes(a.membre_equipe_id))
      : affectations;

    return affectationsFiltered.map(aff => {
      // Déterminer le type d'affectation
      const isRetard = aff.notes?.startsWith('Retard chantier');
      const isReprise = aff.notes === 'Reprise chantier';
      const typeAffectation = isRetard ? 'retard' : isReprise ? 'reprise' : 'normale';

      return {
        membre_equipe_id: aff.membre_equipe_id,
        membre_nom: aff.membre?.nom || '',
        membre_prenom: aff.membre?.prenom || '',
        equipe_id: aff.equipe_id,
        date_debut_originale: aff.date_debut,
        date_fin_originale: aff.date_fin,
        heures_par_jour: aff.heures_par_jour || 8,
        taux_horaire: aff.taux_horaire_specifique || aff.membre?.taux_horaire || 0,
        jours_details: aff.jours_details,
        notes: aff.notes || null,
        type_affectation: typeAffectation,
      };
    });
  }, []);

  // Suspendre le chantier
  const suspendreChantier = useCallback(async (formData: SuspensionFormData) => {
    setLoading(true);
    try {
      // 1. Créer le snapshot des affectations
      const affectationsASuspendre = formData.type_suspension === 'partielle'
        ? affectationsActuelles.filter(a => formData.membres_suspendus.includes(a.membre_equipe_id))
        : affectationsActuelles;

      const snapshot = creerSnapshotAffectations(
        affectationsActuelles,
        formData.type_suspension === 'partielle' ? formData.membres_suspendus : undefined
      );

      // 2. Insérer la suspension
      const { data: suspension, error: suspensionError } = await supabase
        .from('suspensions_chantiers')
        .insert({
          chantier_id: chantierId,
          date_suspension: formData.date_suspension,
          derniere_demi_journee: formData.derniere_demi_journee,
          raison: formData.raison,
          raison_detail: formData.raison === 'autre' ? formData.raison_detail : null,
          duree_estimee: formData.duree_estimee,
          // Si durée personnalisée, envoyer la date manuelle. Sinon null (trigger PostgreSQL calculera)
          date_reprise_estimee: formData.duree_estimee === 'personnalisee'
            ? formData.date_reprise_estimee
            : null,
          type_suspension: formData.type_suspension,
          affectations_suspendues: snapshot,
          cout_mobilisation: 0, // Coût de remobilisation désactivé
        })
        .select()
        .single();

      if (suspensionError) {
        console.error('[useSuspension] Erreur INSERT détaillée:', {
          code: suspensionError.code,
          message: suspensionError.message,
          details: suspensionError.details,
          hint: suspensionError.hint,
        });
        throw suspensionError;
      }

      // 4. Récupérer TOUTES les affectations post-date de suspension (normales + retard + reprise)
      // Important: on refait une requête pour être sûr d'avoir TOUT, y compris les affectations de retard
      const { data: toutesAffectationsFutures } = await supabase
        .from('affectations_chantiers')
        .select('id, date_debut, date_fin, notes, membre_equipe_id')
        .eq('chantier_id', chantierId)
        .gt('date_fin', formData.date_suspension);

      const affectationsFutures = toutesAffectationsFutures || [];

      // Séparer : celles qui commencent APRÈS suspension (supprimer) vs celles qui chevauchent (tronquer)
      const aSupprimer = affectationsFutures.filter(
        aff => aff.date_debut > formData.date_suspension
      );
      const aTronquer = affectationsFutures.filter(
        aff => aff.date_debut <= formData.date_suspension
      );

      if (import.meta.env.DEV) console.log(`[useSuspension] Affectations futures trouvées: ${affectationsFutures.length} (à supprimer: ${aSupprimer.length}, à tronquer: ${aTronquer.length})`);

      // 4a. SUPPRIMER les affectations qui commencent après la date de suspension
      // (inclut les affectations de retard et reprise futures)
      if (aSupprimer.length > 0) {
        const idsASupprimer = aSupprimer.map(a => a.id);
        const { error: deleteError } = await supabase
          .from('affectations_chantiers')
          .delete()
          .in('id', idsASupprimer);

        if (deleteError) {
          console.error('[useSuspension] Erreur suppression affectations futures:', deleteError);
        } else {
          if (import.meta.env.DEV) console.log(`[useSuspension] ${aSupprimer.length} affectation(s) supprimée(s)`);
        }
      }

      // 4b. TRONQUER les affectations qui chevauchent la date de suspension
      for (const aff of aTronquer) {
        const nouveauxJours = calculerJoursOuvres(aff.date_debut, formData.date_suspension);
        const { error: updateError } = await supabase
          .from('affectations_chantiers')
          .update({
            date_fin: formData.date_suspension,
            jours_travailles: Math.max(1, nouveauxJours),
          })
          .eq('id', aff.id);

        if (updateError) {
          console.error('[useSuspension] Erreur troncature affectation:', aff.id, updateError);
        }
      }
      if (aTronquer.length > 0) {
        if (import.meta.env.DEV) console.log(`[useSuspension] ${aTronquer.length} affectation(s) tronquée(s)`);
      }

      // 5. Si suspension TOTALE, mettre le chantier en état suspendu
      if (formData.type_suspension === 'totale') {
        const { error: chantierError } = await supabase
          .from('chantiers')
          .update({ etat_chantier: 'suspendu' })
          .eq('id', chantierId);

        if (chantierError) {
          console.error('[useSuspension] Erreur mise à jour chantier:', chantierError);
        }
      }

      // 6. Collecter et libérer TOUS les membres des affectations supprimées/tronquées
      // (inclut les membres des équipes de retard et reprise)
      const tousMembreIds = [
        ...new Set(
          affectationsFutures
            .map(a => a.membre_equipe_id)
            .filter(Boolean) as string[]
        )
      ];

      if (tousMembreIds.length > 0) {
        // Ne passer en "disponible" que les membres qui étaient "sur_chantier"
        const { error: membresError } = await supabase
          .from('membres_equipe')
          .update({ statut: 'disponible' })
          .in('id', tousMembreIds)
          .eq('statut', 'sur_chantier');

        if (membresError) {
          console.error('[useSuspension] Erreur mise à jour membres:', membresError);
        } else {
          if (import.meta.env.DEV) console.log(`[useSuspension] ${tousMembreIds.length} membre(s) libéré(s)`);
        }
      }

      // Recalculer les métriques AVANT de notifier les autres composants
      if (import.meta.env.DEV) console.log('[useSuspension] 🔄 Recalcul des métriques après suspension...');
      const { data: metricsData, error: metricsError } = await supabase
        .rpc('compute_chantier_metrics', { p_chantier: chantierId });

      if (metricsError) {
        console.error('[useSuspension] ❌ Erreur recalcul métriques:', metricsError);
      } else if (metricsData) {
        // Stocker les métriques recalculées pour le realtime
        await supabase
          .from('chantier_metrics_realtime')
          .upsert({
            chantier_id: chantierId,
            metrics: metricsData,
            updated_at: new Date().toISOString()
          });
        if (import.meta.env.DEV) console.log('[useSuspension] ✅ Métriques rafraîchies après suspension');
      }

      // Notifier les autres composants du changement de métriques (refresh temps réel)
      window.dispatchEvent(new CustomEvent('chantier-metrics-updated', {
        detail: { chantierId }
      }));

      // Notifier le changement de statut pour synchronisation des vues (seulement si totale)
      if (formData.type_suspension === 'totale') {
        window.dispatchEvent(new CustomEvent('chantier-status-updated', {
          detail: { chantierId, oldStatus: 'en_cours', newStatus: 'suspendu' }
        }));
      }

      // Invalider le cache React Query (cascade complète : métriques, finances, charts, alertes)
      if (entrepriseId) {
        invalidateChantier(queryClient, entrepriseId, chantierId);
        invalidateAlertes(queryClient, entrepriseId);
      }

      // Message de succès
      const nbMembres = tousMembreIds.length;
      if (formData.type_suspension === 'totale') {
        toast.success(`Chantier suspendu. ${nbMembres} membre${nbMembres > 1 ? 's' : ''} libéré${nbMembres > 1 ? 's' : ''}.`);
      } else {
        const nbRestants = affectationsActuelles.length - nbMembres;
        toast.success(`${nbMembres} membre${nbMembres > 1 ? 's' : ''} suspendu${nbMembres > 1 ? 's' : ''}. ${nbRestants} membre${nbRestants > 1 ? 's' : ''} toujours actif${nbRestants > 1 ? 's' : ''} sur le chantier.`);
      }

      onSuccess?.();
      return suspension;
    } catch (error) {
      console.error('[useSuspension] Erreur suspension:', error);
      toast.error('Erreur lors de la suspension du chantier.');
      throw error;
    } finally {
      setLoading(false);
    }
  }, [chantierId, affectationsActuelles, creerSnapshotAffectations, onSuccess]);

  // Reprendre le chantier
  const reprendreChantier = useCallback(async (formData: RepriseFormData) => {
    if (import.meta.env.DEV) console.log('[useSuspension] reprendreChantier appelé avec:', {
      formData,
      suspensionActive,
      chantierId,
    });

    if (!suspensionActive) {
      console.error('[useSuspension] Pas de suspension active!');
      toast.error('Aucune suspension active à reprendre.');
      return;
    }

    setLoading(true);
    try {
      const today = format(new Date(), 'yyyy-MM-dd');

      // 1. Créer les nouvelles affectations
      let nouvellesAffectations: any[] = [];

      // Utiliser jours_travailles du formulaire (jours calendaires inclusifs)
      // Cela évite l'erreur CHECK constraint quand les dates tombent sur un weekend
      // (differenceInBusinessDays retournerait 0)
      const joursOuvres = Math.max(1, formData.jours_travailles || 1);

      if (import.meta.env.DEV) console.log('[useSuspension] Jours travaillés depuis formulaire:', {
        date_reprise: formData.date_reprise,
        date_fin: formData.date_fin,
        jours_travailles: formData.jours_travailles,
        joursOuvres,
      });

      if (formData.type_reprise === 'equipe_origine') {
        // Recréer les affectations depuis le snapshot
        nouvellesAffectations = suspensionActive.affectations_suspendues.map(aff => ({
          chantier_id: chantierId,
          membre_equipe_id: aff.membre_equipe_id,
          equipe_id: aff.equipe_id,
          date_debut: formData.date_reprise,
          date_fin: formData.date_fin,
          heures_par_jour: formData.heures_par_jour || aff.heures_par_jour,
          taux_horaire_specifique: aff.taux_horaire,
          jours_travailles: joursOuvres,
          notes: 'Reprise chantier',
        }));
      } else if (formData.type_reprise === 'nouvelle_equipe') {
        // Créer les affectations avec les nouveaux membres/équipes sélectionnés
        const heuresParJour = formData.heures_par_jour || 8; // Défaut 8h
        if (formData.membres_selectionnes && formData.membres_selectionnes.length > 0) {
          nouvellesAffectations = formData.membres_selectionnes.map(membreId => ({
            chantier_id: chantierId,
            membre_equipe_id: membreId,
            date_debut: formData.date_reprise,
            date_fin: formData.date_fin,
            heures_par_jour: heuresParJour,
            jours_travailles: joursOuvres,
            notes: 'Reprise chantier',
          }));
        }
        if (formData.equipes_selectionnees && formData.equipes_selectionnees.length > 0) {
          const affEquipes = formData.equipes_selectionnees.map(equipeId => ({
            chantier_id: chantierId,
            equipe_id: equipeId,
            date_debut: formData.date_reprise,
            date_fin: formData.date_fin,
            heures_par_jour: heuresParJour,
            jours_travailles: joursOuvres,
            notes: 'Reprise chantier',
          }));
          nouvellesAffectations = [...nouvellesAffectations, ...affEquipes];
        }
      }

      // Insérer les nouvelles affectations
      if (import.meta.env.DEV) console.log('[useSuspension] Nouvelles affectations à créer:', nouvellesAffectations);
      if (nouvellesAffectations.length > 0) {
        const { data: affData, error: affError } = await supabase
          .from('affectations_chantiers')
          .insert(nouvellesAffectations)
          .select();

        if (affError) {
          console.error('[useSuspension] Erreur INSERT affectations:', {
            code: affError.code,
            message: affError.message,
            details: affError.details,
            hint: affError.hint,
          });
          throw affError;
        }
        if (import.meta.env.DEV) console.log('[useSuspension] Affectations créées:', affData);
      }

      // 2. Calculer la durée de suspension
      const dateSuspension = new Date(suspensionActive.date_suspension);
      const dateReprise = new Date(formData.date_reprise);
      const dureeJours = differenceInBusinessDays(dateReprise, dateSuspension);

      // 3. Mettre à jour la suspension (date_reprise, durée, type_reprise)
      const { error: suspError } = await supabase
        .from('suspensions_chantiers')
        .update({
          date_reprise: formData.date_reprise,
          duree_suspension_jours: dureeJours,
          type_reprise: formData.type_reprise,
        })
        .eq('id', suspensionActive.id);

      if (suspError) throw suspError;

      // 4. Remettre le chantier en cours ET mettre à jour la date de fin
      const { error: chantierError } = await supabase
        .from('chantiers')
        .update({
          etat_chantier: 'en_cours',
          date_fin_estimee: formData.date_fin,  // Nouvelle date de fin après reprise
        })
        .eq('id', chantierId);

      if (chantierError) {
        console.error('[useSuspension] Erreur mise à jour chantier:', chantierError);
      }

      // 5. Mettre à jour le statut des membres (seulement ceux qui sont disponibles)
      const membreIds = nouvellesAffectations
        .filter(a => a.membre_equipe_id)
        .map(a => a.membre_equipe_id);

      if (membreIds.length > 0) {
        // Ne passer en "sur_chantier" que les membres qui sont "disponible"
        // Les membres en repos, arrêt ou rotation gardent leur statut actuel
        const { error: membresError } = await supabase
          .from('membres_equipe')
          .update({ statut: 'sur_chantier' })
          .in('id', membreIds)
          .eq('statut', 'disponible');

        if (membresError) {
          console.error('[useSuspension] Erreur mise à jour membres:', membresError);
        }
      }

      // Recalculer les métriques AVANT de notifier les autres composants
      if (import.meta.env.DEV) console.log('[useSuspension] 🔄 Recalcul des métriques après reprise...');
      const { data: metricsDataReprise, error: metricsErrorReprise } = await supabase
        .rpc('compute_chantier_metrics', { p_chantier: chantierId });

      if (metricsErrorReprise) {
        console.error('[useSuspension] ❌ Erreur recalcul métriques reprise:', metricsErrorReprise);
      } else if (metricsDataReprise) {
        // Stocker les métriques recalculées pour le realtime
        await supabase
          .from('chantier_metrics_realtime')
          .upsert({
            chantier_id: chantierId,
            metrics: metricsDataReprise,
            updated_at: new Date().toISOString()
          });
        if (import.meta.env.DEV) console.log('[useSuspension] ✅ Métriques rafraîchies après reprise');
      }

      // Notifier les autres composants du changement de métriques (refresh temps réel)
      window.dispatchEvent(new CustomEvent('chantier-metrics-updated', {
        detail: { chantierId }
      }));

      // Notifier le changement de statut pour synchronisation des vues
      window.dispatchEvent(new CustomEvent('chantier-status-updated', {
        detail: { chantierId, oldStatus: 'suspendu', newStatus: 'en_cours' }
      }));

      // Invalider le cache React Query (cascade complète : métriques, finances, charts, alertes)
      if (entrepriseId) {
        invalidateChantier(queryClient, entrepriseId, chantierId);
        invalidateAlertes(queryClient, entrepriseId);
      }

      toast.success(`Chantier repris avec ${nouvellesAffectations.length} membre${nouvellesAffectations.length > 1 ? 's' : ''}.`);
      onSuccess?.();
    } catch (error) {
      console.error('[useSuspension] Erreur reprise:', error);
      toast.error('Erreur lors de la reprise du chantier.');
      throw error;
    } finally {
      setLoading(false);
    }
  }, [chantierId, suspensionActive, onSuccess, queryClient]);

  // Charger l'historique des suspensions
  const loadHistoriqueSuspensions = useCallback(async (): Promise<SuspensionInfo[]> => {
    try {
      const { data, error } = await supabase
        .from('suspensions_chantiers')
        .select('*')
        .eq('chantier_id', chantierId)
        .order('date_suspension', { ascending: false });

      if (error) throw error;
      return (data || []) as SuspensionInfo[];
    } catch (error) {
      console.error('[useSuspension] Erreur chargement historique:', error);
      return [];
    }
  }, [chantierId]);

  return {
    loading,
    suspensionActive,
    affectationsActuelles,
    loadSuspensionActive,
    loadAffectationsActuelles,
    suspendreChantier,
    reprendreChantier,
    loadHistoriqueSuspensions,
    creerSnapshotAffectations,
  };
}
